// Third party
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

// API
import { useTranslation } from 'react-i18next';
import { usePostHog } from 'posthog-js/react';
import { TeamController } from '../../services';

import { sortByOnlineMember } from '../../utils/helpers';

// Redux-toolkit
import { getAuthenticateUser, getTokenDetails } from '../../store/authenticate/selectors';
import { getRegisteredUser } from '../../store/onboarding/selector';
import { memberActions } from '../../store/team/slice';
import { getMembers } from '../../store/team/selectors';
import { commonActions } from '../../store/common/slice';
import { authActions } from '../../store/authenticate/slice';

// Hooks
import { useAppDispatch, useAppSelector } from '../useAppStore';
import { useTeam } from '../useTeam';

// Types
import { IBaseResponse, TAxiosError, TGetTeamResponse } from '../../types/responses';
import { TAddMembersReq, TUpdateMemberReq } from '../../types/requests';
import { QUERY_KEYS } from '../../utils/enums';
import { useError } from '../useCommon';
import { TMember } from '../../types';

export function useGetTeamAPI(isMainTeamPage = false) {
  const dispatch = useAppDispatch();
  const { getMembersByVisibility } = useTeam();
  const { handleReqError } = useError();

  const { personnel } = useAppSelector(getAuthenticateUser);

  return useQuery(
    [`${QUERY_KEYS.TEAM}-${personnel?.email}`],
    async () => {
      const data = {
        email: personnel.email,
        shopName: personnel.shopName,
      };

      const rsp = await TeamController().getTeam<TGetTeamResponse>(data);

      if (rsp?.success && 'team' in rsp) {
        const sortedTeam = sortByOnlineMember(getMembersByVisibility(rsp.team), personnel.email);

        const shopifyIdToMember = sortedTeam.reduce((acc: { [key: string]: TMember }, member) => {
          acc[member.shopifyId] = member;
          return acc;
        }, {});

        dispatch(memberActions.setShopifyIdToMemberMap(shopifyIdToMember));
        dispatch(memberActions.setMembers(sortedTeam));
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, data);
      } else if ('msg' in rsp) {
        console.error(`ERROR! get team data failed! ${rsp.msg}`);
      }

      return rsp;
    },
    {
      staleTime: 3 * (60 * 1000),
      suspense: false,
      enabled: isMainTeamPage,
    },
  );
}

type TAddMembersOptions = {
  newMember: TAddMembersReq['membersData']
  sendInviteEmails?: boolean
};

export function useAddMembersAPI(inOnboarding?: boolean) {
  const queryClient = useQueryClient();
  const { personnel } = useAppSelector(getAuthenticateUser);
  const dispatch = useAppDispatch();
  const getUser = useAppSelector(getRegisteredUser);
  const { handleReqError } = useError();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation('team');
  const posthog = usePostHog();

  const addMembers = (options: TAddMembersOptions) => {
    const members = {
      membersData: Array.isArray(options.newMember) ? options.newMember : [options.newMember],
      shopName: getUser?.shopName || personnel?.shopName,
      sendInviteEmails: options?.sendInviteEmails,
    };

    return TeamController().addMembers<IBaseResponse>(members);
  };

  return useMutation(addMembers, {
    onSuccess: async (rsp, variables) => {
      if (rsp?.success) {
        if (!inOnboarding) {
          dispatch(commonActions.setIsOpenPagesNav(false));
        }

        await queryClient.invalidateQueries({ queryKey: [`${QUERY_KEYS.TEAM}-${personnel?.email}`] });

        posthog.capture('ManageMate_web', {
          shopName: personnel?.shopName,
          newMember: variables.newMember,
          addedAt: new Date().toISOString(),
          action: 'member_added',
        });

        enqueueSnackbar(t('memberWasAdded'), { variant: 'success' });
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, variables);
      } else if ('msg' in rsp) {
        console.error(`ERROR! add members failed! ${rsp.msg}`);
      }
    },
    onError: (error: TAxiosError) => {
      console.error(`ERROR! add members request threw an Exception! ${error}`);
    },
  });
}

export function useUpdateMemberAPI() {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { getMembersByVisibility, updateMembers } = useTeam();
  const { t } = useTranslation('team');

  const authenticateUser = useAppSelector(getAuthenticateUser);
  const tokenDetails = useAppSelector(getTokenDetails);
  const { handleReqError } = useError();

  const updateMember = async (member: TUpdateMemberReq['staffMemberData']) => {
    const updateData = {
      shopName: authenticateUser.personnel.shopName,
      email: member.email,
      staffMemberData: member,
    };

    return TeamController().updateMember<IBaseResponse>(updateData);
  };
  return useMutation(updateMember, {
    onSuccess: async (rsp, variables) => {
      if (rsp?.success) {
        if (variables.permissionLevel !== tokenDetails.permissions.permissionLevel
          && variables.email === authenticateUser.personnel.email) {
          dispatch(authActions.setTokenDetails({
            ...tokenDetails,
            permissions: {
              ...tokenDetails.permissions,
              permissionLevel: variables.permissionLevel,
              clockIn: variables.clockIn,
            },
          }));

          dispatch(authActions.setAuthenticateUser({
            ...authenticateUser,
            personnel: {
              ...authenticateUser.personnel,
              permissionLevel: variables.permissionLevel,
            },
          }));
        } else {
          dispatch(authActions.setTokenDetails({
            ...tokenDetails,
            permissions: {
              ...tokenDetails.permissions,
              clockIn: variables.clockIn,
            },
          }));
          dispatch(authActions.setAuthenticateUser({
            ...authenticateUser,
            personnel: {
              ...authenticateUser.personnel,
              locations: variables.locations,
            },
          }));
        }

        const updatedMembers = updateMembers(variables);

        dispatch(memberActions.setMembers(sortByOnlineMember(getMembersByVisibility(updatedMembers), authenticateUser.personnel.email)));
        dispatch(commonActions.setIsOpenPagesNav(false));
        enqueueSnackbar(t('teamMemberWasUpdated'), { variant: 'success' });
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, variables);
      }
    },
    onError: (error: TAxiosError) => {
      console.error(`ERROR! update member request threw an Exception! ${error}`);
    },
  });
}

export function useDeleteMemberAPI() {
  const { getMembersByVisibility } = useTeam();
  const { personnel } = useAppSelector(getAuthenticateUser);
  const members = useAppSelector(getMembers);
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation('team');
  const { handleReqError } = useError();
  const posthog = usePostHog();

  const deleteMember = (email: string) => {
    const data = {
      email,
      shopName: personnel.shopName,
    };

    return TeamController().deleteMember<IBaseResponse>(data);
  };

  return useMutation(deleteMember, {
    onSuccess: async (rsp, variables) => {
      if (rsp?.success) {
        const formattedMembers = members.filter((member) => member.email !== variables);
        const sortedTeam = sortByOnlineMember(getMembersByVisibility(formattedMembers), personnel.email);

        dispatch(memberActions.setMembers(sortedTeam));
        dispatch(memberActions.toggleDeleteDialog(false));

        posthog.capture('ManageMate_web', {
          shopName: personnel?.shopName,
          email: variables,
          addedAt: new Date().toISOString(),
          action: 'member_deleted',
        });

        enqueueSnackbar(`${t('teamMemberWasDeleted')}`, { variant: 'success' });
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, variables);
      } else if ('msg' in rsp) {
        console.error(`ERROR! delete member request failed! ${rsp.msg}`);
      }
    },
    onError: (error: TAxiosError) => {
      console.error(`ERROR! delete member request threw an Exception! ${error}`);
    },
  });
}

export function useResendInvitationAPI() {
  const { t } = useTranslation('common');
  const { personnel } = useAppSelector(getAuthenticateUser);
  const { enqueueSnackbar } = useSnackbar();
  const { handleReqError } = useError();

  const resendInvitationApi = (email: string) => {
    const data = {
      email,
      shopName: personnel.shopName,
      invitorName: personnel.name,
    };

    return TeamController().reInvite<IBaseResponse>(data);
  };

  return useMutation(resendInvitationApi, {
    onSuccess: (rsp, variables) => {
      if (rsp.success) {
        enqueueSnackbar(`${t('emailSent')}`, {
          variant: 'success',
        });
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, variables);
      } else if ('msg' in rsp) {
        console.error(`ERROR! resend invitation failed! ${rsp.msg}`);
      }

      return rsp;
    },
    onError: (err: TAxiosError) => {
      console.error(
        `ERROR! resend invitation request threw an Exception! ${err}`,
      );
    },
  });
}

export function useSetLocaleAPI() {
  const { personnel } = useAppSelector(getAuthenticateUser);
  const dispatch = useAppDispatch();
  const { handleReqError } = useError();
  const tokenDetails = useAppSelector(getTokenDetails);
  const posthog = usePostHog();

  const addLocaleApi = (payload: { locale: string }) => {
    const data = {
      shopName: personnel.shopName,
      email: personnel.email,
      locale: payload.locale,
    };

    return TeamController().setLocale<IBaseResponse>(data);
  };

  return useMutation(addLocaleApi, {
    onSuccess: async (rsp, variables) => {
      if (rsp?.success) {
        dispatch(authActions.setTokenDetails({
          ...tokenDetails,
          settings: {
            ...tokenDetails.settings,
            ui: {
              ...tokenDetails.settings.ui,
              locale: variables.locale,
            },
          },
        }));

        posthog.capture('ManageMate_web', {
          shopName: personnel?.shopName,
          email: personnel?.email,
          addedAt: new Date().toISOString(),
          action: 'locale_changed',
        });
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, variables);
      } else if ('msg' in rsp) {
        console.error(`ERROR! set locale failed! ${rsp.msg}`);
      }
    },
    onError: (error: TAxiosError) => {
      console.error(`ERROR! set locale request threw an Exception! ${error}`);
    },
  });
}

export function useDateFormatAPI() {
  const { personnel } = useAppSelector(getAuthenticateUser);
  const dispatch = useAppDispatch();
  const { handleReqError } = useError();
  const tokenDetails = useAppSelector(getTokenDetails);
  const posthog = usePostHog();

  const addDateFormatApi = (payload: { dateFormat: string }) => {
    const data = {
      shopName: personnel.shopName,
      email: personnel.email,
      dateFormat: payload.dateFormat,
    };

    return TeamController().setDateFormat<IBaseResponse>(data);
  };

  return useMutation(addDateFormatApi, {
    onSuccess: async (rsp, variables) => {
      if (rsp?.success) {
        dispatch(authActions.setTokenDetails({
          ...tokenDetails,
          settings: {
            ...tokenDetails.settings,
            ui: {
              ...tokenDetails.settings.ui,
              dateFormat: variables.dateFormat,
            },
          },
        }));

        posthog.capture('ManageMate_web', {
          shopName: personnel?.shopName,
          email: personnel?.email,
          addedAt: new Date().toISOString(),
          action: 'date_format_changed',
        });
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, variables);
      } else if ('msg' in rsp) {
        console.error(`ERROR! set date format failed! ${rsp.msg}`);
      }
    },
    onError: (error: TAxiosError) => {
      console.error(`ERROR! date format request threw an Exception! ${error}`);
    },
  });
}
